(() => {
  if (customElements.get("product-selector")) {
    return;
  }

  class PdpProductSelector extends HTMLElement {
    constructor() {
      super();

      this.mediaRevealTimeoutId = null;

      this.addEventListener(
        "change",
        debounce(this.onOptionChange.bind(this), 200),
      );
    }

    connectedCallback() {
      this.initSlider();
      this.initCustomPropListener();
      this.checkVariantFromURL();
      this.handleOnLoadSelectedInputs();
      this.syncCoverDesignAndFormatAvailability();
    }

    shouldDisableClassicArchiveAndSoftback() {
      const coverFinishInput = this.querySelector(
        "input[name='properties[customisation.cover_finish]']:checked",
      );

      return coverFinishInput?.value === "foil";
    }

    updateOptionState(shouldDisableOption, key, value) {
      const optionInput = this.querySelector(
        `input[name='properties[customisation.${key}]'][value='${value}']`,
      );

      if (!optionInput) {
        return null;
      }

      optionInput.disabled = shouldDisableOption;

      const optionLabel = this.querySelector(`label[for='${optionInput.id}']`);
      if (optionLabel) {
        optionLabel.classList.toggle("opacity-50", shouldDisableOption);
        optionLabel.classList.toggle("cursor-not-allowed", shouldDisableOption);
      }

      return optionInput;
    }

    selectAlternativeOption(field) {
      const firstAvailableOptionInput = this.querySelector(
        `input[name='properties[customisation.${field}]']:not(:disabled)`,
      );

      if (!firstAvailableOptionInput) {
        return false;
      }

      firstAvailableOptionInput.checked = true;
      firstAvailableOptionInput.dispatchEvent(
        new Event("change", { bubbles: true }),
      );

      return true;
    }

    syncCoverDesignAndFormatAvailability() {
      const shouldDisableClassicArchiveAndSoftback =
        this.shouldDisableClassicArchiveAndSoftback();
      const coverDesignClassicArchiveInput = this.updateOptionState(
        shouldDisableClassicArchiveAndSoftback,
        "cover_design",
        "classic",
      );
      const coverFormatSoftbackInput = this.updateOptionState(
        shouldDisableClassicArchiveAndSoftback,
        "format",
        "softback",
      );

      if (shouldDisableClassicArchiveAndSoftback) {
        if (
          coverDesignClassicArchiveInput &&
          coverDesignClassicArchiveInput.checked
        ) {
          this.selectAlternativeOption("cover_design");
        }

        if (coverFormatSoftbackInput && coverFormatSoftbackInput.checked) {
          this.selectAlternativeOption("format");
        }
      }
    }

    initSlider() {
      const sliderList = this.querySelectorAll(".splide");

      if (!sliderList.length) return;

      const mainSettings = {
        trimSpace: "move",
        autoWidth: true,
        waitForTransition: true,
        pagination: false,
        arrows: true,
        focus: 0,
        omitEnd: true,
        speed: 300,
        easing: "ease-in-out",
        gap: "0.8rem",
      };

      sliderList.forEach(function (sliderListEl) {
        const slider = new Splide(sliderListEl, mainSettings);

        // Turn off arrows and drag when not enough elements
        slider.on("ready resize", () => {
          setTimeout(() => {
            const TOLERANCE = 4;

            const childrenWidth = [...slider.Components.Elements.slides].reduce(
              (acc, val) => {
                const style = val.currentStyle || window.getComputedStyle(val);
                acc += val.clientWidth + parseInt(style.marginRight);
                return acc;
              },
              0,
            );

            const wrapperWidth =
              slider.Components.Elements.list.clientWidth + TOLERANCE;

            if (childrenWidth <= wrapperWidth) {
              slider.options = {
                arrows: false,
                drag: false,
              };
            } else {
              slider.options = {
                arrows: true,
                drag: true,
              };
              sliderListEl.classList.add("is-overflow");
              sliderListEl.parentElement.classList.add("is-slider-active");
            }
          }, 200);
        });

        slider.mount();
      });
    }

    initCustomPropListener() {
      const colour_variable =
        document.querySelector(".colour-variable")?.textContent;
      const colourVal = document.querySelectorAll(
        `input[name='properties[${colour_variable}]']`,
      );

      if (colourVal) {
        colourVal.forEach((val) => {
          val.addEventListener("change", (e) => {
            const colour = e.target.value;
            const variantOptions =
              this.currentVariant?.options || this.getVariantData()[0].options;
            if (colour) {
              const formattedVariants = variantOptions
                .map((item) => item.toLowerCase().replace(/\s+/g, "-"))
                .join("-");
              this.updateMedia(colour + "-" + formattedVariants);
            }
          });
        });
      }
    }

    checkVariantFromURL() {
      const urlParams = new URLSearchParams(window.location.search);
      const variantId = urlParams.get("variant");

      if (variantId) {
        this.currentVariant = this.getVariantData().find(
          (variant) => variant.id === parseInt(variantId),
        );
        if (this.currentVariant) {
          this.updateVariantInput();
        }
      }
    }

    handleOnLoadSelectedInputs() {
      const coverDesignInput = document.querySelector(
        "input[data-name='cover design']:checked",
      );

      const colour_variable =
        document.querySelector(".colour-variable")?.textContent;
      const colourInput = document.querySelector(
        `input[name='properties[${colour_variable}]']:checked`,
      );
      if (coverDesignInput && colourInput) {
        const selectedColour = colourInput.value;
        const variantOptions =
          this.currentVariant?.options || this.getVariantData()[0].options;

        if (variantOptions && selectedColour) {
          const formattedVariants = variantOptions
            .map((item) => item.toLowerCase().replace(/\s+/g, "-"))
            .join("-");
          const customId = `${selectedColour}-${formattedVariants}`;
          this.updateMedia(customId);
        }
      }
    }

    onOptionChange(event) {
      this.updateOptions(event);
      this.syncCoverDesignAndFormatAvailability();
      this.updateMasterId();

      if (!this.currentVariant) {
        this.toggleAddButton(true, window.theme.strings.product.unavailable);
        this.setUnavailable();
        return;
      }

      if (!this.currentVariant.available) {
        this.toggleAddButton(true, window.theme.strings.product.soldOut);
        window.toggleButtonState();
      } else {
        this.toggleAddButton(false, window.theme.strings.product.addToCart);
        window.toggleButtonState();
      }

      const colour_variable =
        document.querySelector(".colour-variable")?.textContent;
      const colourVal = document.querySelector(
        `input[name='properties[${colour_variable}]']:checked`,
      )?.value;

      if (colourVal) {
        const variantOptions = this.currentVariant.options;
        const formattedVariants = variantOptions
          .map((item) => item.toLowerCase().replace(/\s+/g, "-"))
          .join("-");
        this.updateMedia(colourVal + "-" + formattedVariants);
      } else {
        this.updateMedia();
      }

      if (!this.closest("modal-dialog")) this.updateURL();
      this.updateVariantInput();
    }

    updateOptions(event) {
      const { name, value } = event.target;
      if (name && value) {
        const targetInstance = name.includes("options[")
          ? this.querySelector(
              `[name="${
                name.split("options[")[1].split("]")[0]
              }"][value="${value}"]`,
            )
          : this.querySelector(`[name="options[${name}]"]`);

        if (targetInstance) {
          if (targetInstance.type === "select-one") {
            targetInstance.value = value;
          } else if (targetInstance.type === "radio") {
            targetInstance.checked = true;
          }
        }
      }

      this.options = Array.from(
        this.querySelectorAll("select, input:checked"),
        (element) => {
          const valueLabel = this.querySelector(
            `#${element.dataset.valueLabel}`,
          );
          const elementValue = element.dataset.value;
          if (!valueLabel) return elementValue;
          const valueLabelString = valueLabel.hasAttribute("data-option-value")
            ? `${elementValue}`
            : `: ${elementValue}`;
          valueLabel.innerText = valueLabelString;
          return elementValue;
        },
      );
    }

    updateMasterId() {
      this.currentVariant = this.getVariantData().find((variant) => {
        return !variant.options
          .map((option, index) => {
            return this.options[index] === option;
          })
          .includes(false);
      });
    }

    clearPendingMediaReveal() {
      if (this.mediaRevealTimeoutId != null) {
        clearTimeout(this.mediaRevealTimeoutId);
        this.mediaRevealTimeoutId = null;
      }
    }

    scheduleMediaReveal(mainMedia, shouldDelay) {
      this.clearPendingMediaReveal();

      if (shouldDelay) {
        /*
         * Add a short 10ms delay before removing the hidden class so the correct active image is shown for the consolidated birthday milestone template.
         */
        this.mediaRevealTimeoutId = setTimeout(() => {
          mainMedia.classList.remove("hidden");
          this.mediaRevealTimeoutId = null;
        }, 10);
        return;
      }
      mainMedia.classList.remove("hidden");
    }

    isConsolidateBdayMstoneTemplate() {
      return (
        typeof window.currentTemplate !== "undefined" &&
        window.currentTemplate &&
        window.currentTemplate.suffix === "consolidate-bday-mstone"
      );
    }

    updateMedia(custom_id) {
      if (!custom_id && !this.currentVariant.featured_media) return;

      const productMedia = document.querySelector("pdp-product-media");

      if (!productMedia) {
        return;
      }

      document
        .querySelectorAll(".pdp_header__cover-img-item")
        .forEach((media) => {
          media.classList.add("hidden");
        });

      const mediaItems = productMedia.querySelectorAll(
        ".pdp_header__cover-img-item",
      );
      if (!mediaItems) {
        return;
      }

      let mainMedia = mediaItems[0]; // Default to first media

      const mediaId = custom_id || this.currentVariant.featured_media.id;
      let normalizedMediaId =
        typeof mediaId === "string"
          ? mediaId.toLowerCase().replace(/\s+/g, "-")
          : "";

      // Try to find media by normalizedMediaId
      let media = normalizedMediaId
        ? productMedia.querySelector(`[data-media-id="${normalizedMediaId}"]`)
        : null;

      if (!media && normalizedMediaId) {
        // Fallback: remove "-without-picture" or "-with-picture" from the end
        const fallbackMediaId = normalizedMediaId.replace(
          /-(without|with)-picture$/,
          "",
        );
        if (fallbackMediaId !== normalizedMediaId) {
          media = productMedia.querySelector(
            `[data-media-id="${fallbackMediaId}"]`,
          );
        }
      }

      if (media) {
        mainMedia = media;
      }

      if (mainMedia) {
        const isConsolidateTemplate = this.isConsolidateBdayMstoneTemplate();

        this.scheduleMediaReveal(mainMedia, isConsolidateTemplate);
      }
    }

    updateURL() {
      if (!window.location.href.includes("/products")) {
        return;
      }

      const searchParams = new URLSearchParams(window.location.search);

      searchParams.set("variant", this.currentVariant.id);

      const isConsolidateTemplate = this.isConsolidateBdayMstoneTemplate();
      const baseUrl = isConsolidateTemplate
        ? window.location.pathname
        : this.dataset.url || window.location.pathname;

      window.history.replaceState(
        {},
        "",
        `${baseUrl}?${searchParams.toString()}`,
      );
    }

    updateVariantInput() {
      const productForms = document.querySelectorAll(
        `#product-form-${this.dataset.section}, #product-form-installment`,
      );

      if (this.hasAttribute("data-variant-id")) {
        this.setAttribute("data-variant-id", this.currentVariant.id);
      }

      productForms.forEach((form) => {
        const input = form.querySelector('[name="id"]');

        input.value = this.currentVariant.id;
        input.dispatchEvent(new Event("change", { bubbles: true }));
      });
    }

    toggleAddButton(disable = true, text) {
      const productForm = document.getElementById(
        `product-form-${this.dataset.section}`,
      );

      if (!productForm) return;

      const addButton = productForm.querySelector('[name="add"]');
      const buyNowButton = productForm.querySelector("[data-fast-checkout]");
      const addButtonText = productForm.querySelector('[name="add"] > span');

      if (!addButton) return;

      if (disable) {
        addButton.setAttribute("disabled", "disabled");
        buyNowButton && buyNowButton.setAttribute("disabled", "disabled");

        if (text) {
          addButtonText.textContent = text;
          addButton.classList.remove("is-loading");
        } else {
          addButton.classList.add("is-loading");
        }
      } else {
        addButton.removeAttribute("disabled");
        buyNowButton && buyNowButton.removeAttribute("disabled");
        addButtonText.textContent = window.theme.strings.product.addToCart;
        addButton.classList.remove("is-loading");
        buyNowButton && buyNowButton.classList.remove("is-loading");
      }
    }

    setUnavailable() {
      const button = document.getElementById(
        `product-form-${this.dataset.section}`,
      );
      const addButton = button.querySelector('[name="add"]');
      const addButtonText = button.querySelector('[name="add"] > span');
      const price = document.getElementById(`price-${this.dataset.section}`);

      if (!addButton) return;

      addButtonText.textContent = window.theme.strings.product.unavailable;

      if (price) price.classList.add("visibility-hidden");
    }

    getVariantData() {
      return JSON.parse(this.querySelector("[data-product-json]").textContent)
        .variants;
    }
  }

  customElements.define("product-selector", PdpProductSelector);
})();
